import React from 'react';
import styled, { css } from 'styled-components';

/**
 * 고른 칸(thumb)의 색.
 *   neutral  기본 — 문서·GTC 사이드바의 "원본 | 위키" 처럼 흰 판 + 옅은 그림자.
 *   brand    사이드바 하단 "사용자 가이드" 배너와 같은 brand 그라디언트 + brightness 필터.
 *            글자는 `brandContrastText` — 선적서류 유무환 토글처럼 값 하나를 힘주어 고를 때.
 */
export type SegmentedTabsTone = 'neutral' | 'brand';

interface Props<T extends string> {
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  tone?: SegmentedTabsTone;
  /** 저장 중처럼 잠시 바꾸지 못하게 할 때 — 모양은 유지하고 입력만 막는다. */
  disabled?: boolean;
}

/** Sliding selection surface shared by the document and GTC sidebars. */
export function SegmentedTabs<T extends string>({
  options, value, onChange, 'aria-label': label, tone = 'neutral', disabled = false,
}: Props<T>) {
  const index = Math.max(0, options.findIndex((option) => option.value === value));
  return <Group role="tablist" aria-label={label} $disabled={disabled}>
    <Thumb aria-hidden $index={index} $count={options.length} $tone={tone} />
    {options.map((option, position) => <Tab key={option.value} type="button" role="tab"
      aria-selected={option.value === value} tabIndex={option.value === value ? 0 : -1}
      disabled={disabled}
      $active={option.value === value} $tone={tone} onClick={() => onChange(option.value)}
      onKeyDown={(event) => {
        const next = event.key === 'ArrowRight' ? (position + 1) % options.length
          : event.key === 'ArrowLeft' ? (position + options.length - 1) % options.length
            : event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : null;
        if (next === null) return;
        event.preventDefault(); onChange(options[next].value);
        event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
      }}>{option.label}</Tab>)}
  </Group>;
}

const Group = styled.div<{ $disabled: boolean }>`
  position: relative; display: flex; padding: 4px;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme }) => theme.colors.bgHover};
  ${({ $disabled }) => $disabled && css`opacity: 0.6;`}
`;
const Thumb = styled.span<{ $index: number; $count: number; $tone: SegmentedTabsTone }>`
  position: absolute; top: 4px; bottom: 4px; left: 4px;
  width: calc((100% - 8px) / ${({ $count }) => $count});
  border-radius: calc(${({ theme }) => theme.sizes.borderRadius} - 4px);
  background: ${({ theme }) => theme.colors.bgPrimary}; box-shadow: ${({ theme }) => theme.shadows.sm};
  transform: translateX(${({ $index }) => $index * 100}%);
  transition: transform ${({ theme }) => theme.transitions.normal}; pointer-events: none;
  /* brand — Sidebar 의 GuideBanner 와 같은 그라디언트·brightness(1.06) 필터. 값이 바뀌면 거기도 함께. */
  ${({ $tone, theme }) => $tone === 'brand' && css`
    background: linear-gradient(135deg, ${theme.colors.brand} 0%, ${theme.colors.brandGradientEnd} 100%);
    box-shadow: 0 2px 10px ${theme.colors.brandSoft};
    filter: brightness(1.06);
  `}
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const Tab = styled.button<{ $active: boolean; $tone: SegmentedTabsTone }>`
  position: relative; z-index: 1; flex: 1; height: 30px;
  display: inline-flex; align-items: center; justify-content: center; border: none;
  border-radius: calc(${({ theme }) => theme.sizes.borderRadius} - 4px);
  background: transparent; font-size: 0.875rem; font-weight: 600;
  color: ${({ $active, theme }) => $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  cursor: pointer; transition: color ${({ theme }) => theme.transitions.normal};
  &:hover { color: ${({ theme }) => theme.colors.textPrimary}; }
  &:disabled { cursor: default; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.brand}; outline-offset: 2px; }
  ${({ $tone, $active, theme }) => $tone === 'brand' && $active && css`
    color: ${theme.colors.brandContrastText};
    &:hover { color: ${theme.colors.brandContrastText}; }
  `}
`;
